import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button as ChackraUIButton } from '@chakra-ui/react';
import styled from '@emotion/styled';

import { Color, Variant, Size } from '@view/theme';
import { Weights } from '@theme/foundations/fontSizes';
import BEIcon from '@molecules/BEIcon';
import { BEIconNameType } from '@molecules/BEIcon/BEIconProps';

export type ButtonProps = {
  children?: string | Element | null;
  label?: string;
  isLoading?: boolean;
  isDisabled?: boolean;
  color?: Color;
  variant?: Variant;
  size?: Size;
  weight?: Weights;
  icon?: BEIconNameType;
  paddingX?: number;
  paddingY?: number;
  width?: string | number;
  minWidth?: string | number;
  disabled?: boolean;
  className?: StyleSheet | any;
  border?: string | number;
  borderColor?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
};

const StyledButton = styled(ChackraUIButton)`
`;

const BEButton: React.FC<ButtonProps> = (props: ButtonProps) => {
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  function onClick(e: React.MouseEvent<HTMLButtonElement>) {
    if (props.onClick) props.onClick(e);
  }

  return (
    <StyledButton
      className={props.className}
      ref={buttonRef}
      isLoading={props.isLoading === true}
      colorScheme={props.color || 'neutral'}
      onClick={onClick}
      variant={props.variant}
      borderRadius={'full'}
      size={props.size}
      paddingX={props.paddingX}
      paddingY={props.paddingY}
      width={props.width}
      minWidth={props.minWidth}
      fontWeight={props.weight || 500}
      isDisabled={props.isDisabled}
      border={props.border}
      borderColor={props.borderColor}
    >
      <>
        {props.icon && <BEIcon name={props.icon} />}
        {props.label}
        {props.children}
      </>
    </StyledButton>
  );
};

export const BELoadingButton = ({
  onClick,
  children,
  disabled = false,
  color = 'primary',
  onBeforeClick,
  onAfterClick,
  ...props
}: ButtonProps & {
  disabled?: boolean;
  onBeforeClick?: () => void;
  onAfterClick?: () => void;
}) => {
  const isMountedRef = useRef(true);
  const [confirmLoading, setConfirmLoading] = useState(!!props.isLoading);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleClick = useCallback(async () => {
    if (!confirmLoading) {
      await (onClick as (() => Promise<void>) | undefined)?.();
    }
  }, [confirmLoading, onClick]);

  const onClickButton = useCallback(async () => {
    onBeforeClick?.();
    setConfirmLoading(true);
    try {
      await handleClick();
    } finally {
      if (isMountedRef.current) setConfirmLoading(false);
    }
    onAfterClick?.();
  }, [handleClick, onAfterClick, onBeforeClick]);

  return (
    <BEButton
      minWidth={24}
      isLoading={confirmLoading}
      color={color}
      isDisabled={disabled}
      onClick={onClickButton}
      {...props}
    >
      {children}
    </BEButton>
  );
};

export default BEButton;
